import { escapeHtml } from "./escapeHtml";
import { HEADER_TOKEN_KEYS } from "./headerTokens";

const ALLOWED_TAGS = new Set(["div", "p", "br", "b", "strong", "i", "em", "u", "span"]);
const VOID_TAGS = new Set(["br"]);
const DROP_WITH_CONTENT = new Set(["script", "style", "iframe", "object", "embed", "svg", "math", "template"]);
const ALLOWED_STYLE_PROPS = new Set(["color", "font-size", "font-weight", "font-style", "text-align", "text-decoration"]);
const SAFE_STYLE_VALUE = /^[#a-zA-Z0-9.,%\s-]+$/;
const ENTITY_AMP = /&(?!(?:#\d+|#x[0-9a-fA-F]+|[a-zA-Z]+);)/g;
const TAG = /<\/?([a-zA-Z0-9-]+)([^>]*)>/g;

function escapeText(text: string): string {
  return text.replace(ENTITY_AMP, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function cleanStyle(style: string): string {
  return style
    .split(";")
    .map((decl) => decl.split(":"))
    .filter(([prop, value]) => prop && value && ALLOWED_STYLE_PROPS.has(prop.trim().toLowerCase())
      && SAFE_STYLE_VALUE.test(value.trim()))
    .map(([prop, value]) => `${prop.trim().toLowerCase()}:${value.trim()}`)
    .join(";");
}

function cleanAttributes(tag: string, attrs: string): string {
  const out: string[] = [];
  const style = /style\s*=\s*"([^"]*)"/i.exec(attrs)?.[1];
  if (style) {
    const cleaned = cleanStyle(style);
    if (cleaned) out.push(`style="${cleaned}"`);
  }
  if (tag === "span") {
    const token = /data-token\s*=\s*"([^"]*)"/i.exec(attrs)?.[1];
    if (token && (HEADER_TOKEN_KEYS as readonly string[]).includes(token)) out.push(`data-token="${token}"`);
  }
  return out.length ? ` ${out.join(" ")}` : "";
}

export function sanitizeHeaderHtml(html: string): string {
  let result = "";
  let cursor = 0;
  let dropDepth = 0;
  let droppedTag = "";
  const open: string[] = [];
  for (const match of html.matchAll(TAG)) {
    const [full, rawName, attrs] = match;
    const name = rawName.toLowerCase();
    const isClose = full.startsWith("</");
    const textBefore = html.slice(cursor, match.index);
    cursor = (match.index ?? 0) + full.length;
    if (dropDepth === 0) result += escapeText(textBefore);
    if (DROP_WITH_CONTENT.has(name)) {
      if (!isClose) { dropDepth += 1; droppedTag = name; } else if (name === droppedTag) { dropDepth = Math.max(0, dropDepth - 1); }
      continue;
    }
    if (dropDepth > 0 || !ALLOWED_TAGS.has(name)) continue;
    if (isClose) {
      if (VOID_TAGS.has(name)) continue;
      const idx = open.lastIndexOf(name);
      if (idx === -1) continue;
      while (open.length > idx) result += `</${open.pop()}>`;
      continue;
    }
    if (VOID_TAGS.has(name)) { result += `<${name}>`; continue; }
    open.push(name);
    result += `<${name}${cleanAttributes(name, attrs)}>`;
  }
  if (dropDepth === 0) result += escapeText(html.slice(cursor));
  while (open.length) result += `</${open.pop()}>`;
  return result;
}

export function plainTextToHeaderHtml(text: string): string {
  return text
    .split(/\r?\n/)
    .map((line) => (line.trim() ? `<div>${escapeHtml(line)}</div>` : "<div><br></div>"))
    .join("");
}
